import { useColorScheme } from './color-scheme';
import claro from './assets/brand/logotipo-claro.png';
import escuro from './assets/brand/logotipo-escuro.png';
import simboloClaro from './assets/brand/simbolo-claro.png';
import simboloEscuro from './assets/brand/simbolo-escuro.png';

/**
 * O logotipo: o livro de registros aberto e o nome.
 *
 * **Duas versões, e a razão é uma só:** o wordmark é quase preto, então num
 * cabeçalho escuro ele desaparece. A variante escura o traz em branco, e traz o
 * livro na tinta clara da paleta - verde pálido na página esquerda, branco na
 * direita, para o vinco central continuar a se ler.
 *
 * As duas saem do mesmo vetor. O símbolo é `assets/brand/simbolo.svg`, e cada
 * PNG desta pasta é gerado dele pelo `gerar.py`: nenhum é arte independente, e
 * a variante escura não é mais uma correção por cima da clara - é o mesmo
 * desenho com outra paleta. Foi o que encerrou o problema de contraste do lápis
 * anterior, cujas duas facetas mais escuras ficavam em 1,75 contra o `#141414`
 * do tema escuro. Nenhuma tinta de agora fica abaixo de 5,4 em fundo nenhum.
 *
 * A escolha é pelo esquema de cor em vigor, não por `prefers-color-scheme` no
 * CSS: quem trocou o tema no seletor da página espera o logotipo acompanhar, e
 * a mídia do CSS não sabe da escolha manual.
 *
 * As duas artes moram no pacote de tema porque a marca é dos três - app, admin
 * e landing -, e porque logotipo que vive dentro de um app tende a divergir do
 * que vive no outro.
 */
export function Logotipo({ altura = 30, forcar, apenasSimbolo }: {
  /**
   * Altura da **arte**, em pixels - e não de uma moldura em volta dela.
   *
   * A distinção já custou caro. Até 03/09/2026 os arquivos vinham com margem
   * transparente sobrando: a arte ocupava 196px de um mestre de 480, e um
   * `altura={24}` desenhava um logotipo de 9,8px dentro de uma caixa de 24 -
   * a marca aparecia miúda em toda tela onde o número dizia que ela era
   * grande. Os mestres agora são o recorte justo, e **os números de chamada
   * foram mantidos de propósito**: é o que faz a marca crescer 2,45 vezes em
   * cada lugar, que era o defeito a corrigir.
   */
  altura?: number;
  /**
   * Fixa a variante, ignorando o tema.
   *
   * Existe para a faixa escura da landing, que é escura **sempre**: ali o
   * logotipo tem de ser o claro mesmo quando a página está em modo claro.
   */
  forcar?: 'claro' | 'escuro';
  /**
   * Só o livro, sem o nome. É o que vai na barra lateral recolhida, onde não
   * cabe wordmark nenhum.
   *
   * **Também são duas artes, e pelo mesmo motivo do logotipo inteiro:** a
   * paleta muda com o fundo. As duas saem do mesmo `simbolo.svg`, então não
   * podem divergir em desenho - só em tinta.
   *
   * `altura` mede a mesma coisa nos dois: o livro tem exatamente a altura da
   * caixa do `T` maiúsculo, 187 dos 196px da arte horizontal, então
   * `apenasSimbolo` e o logotipo inteiro no mesmo número saem com o livro do
   * mesmo tamanho. **O que muda em relação ao lápis é a largura:** o livro é
   * 1,22 vez mais largo que alto, contra 0,73 do lápis, e no mesmo `altura`
   * ocupa 229px em vez de 137. Onde a barra recolhida for estreita, é a largura
   * que aperta primeiro, não a altura.
   */
  apenasSimbolo?: boolean;
}) {
  const { scheme } = useColorScheme();
  const usarClaro = forcar ? forcar === 'escuro' : scheme === 'dark';

  return (
    <img
      src={
        apenasSimbolo ? (usarClaro ? simboloEscuro : simboloClaro) : usarClaro ? escuro : claro
      }
      alt="Titularis"
      /*
       * Altura fixa e largura automática. O `flexShrink` e o `width: auto`
       * não são zelo: dentro de um contêiner flex em coluna, o padrão
       * `align-items: stretch` estica a imagem na largura e amassa o logotipo -
       * foi exatamente o que aconteceu na primeira versão do cartão de
       * compartilhamento.
       */
      style={{ height: altura, width: 'auto', display: 'block', flexShrink: 0 }}
    />
  );
}
